import React from 'react';
import {
  Box,
  Chip,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import type { ReleaseNote, ReleaseNoteStatus } from '../../services/releaseNotes.service';

type ChipColor = 'default' | 'info' | 'success' | 'error';

const STATUS_STYLE: Record<ReleaseNoteStatus, { label: string; color: ChipColor }> = {
  draft: { label: 'Draft', color: 'default' },
  scheduled: { label: 'Scheduled', color: 'info' },
  published: { label: 'Published', color: 'success' },
  failed: { label: 'Send failed', color: 'error' },
};

const AUDIENCE_LABEL: Record<string, string> = {
  all: 'All users',
  orgs: 'Selected orgs',
  users: 'Selected users',
};

const formatDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString() : '—';

interface Props {
  note: ReleaseNote;
  onEdit: (note: ReleaseNote) => void;
  onDelete: (note: ReleaseNote) => void;
  onPublish: (note: ReleaseNote) => void;
  onResend: (note: ReleaseNote) => void;
}

const ReleaseNoteRow: React.FC<Props> = ({
  note,
  onEdit,
  onDelete,
  onPublish,
  onResend,
}) => {
  const status = STATUS_STYLE[note.status] ?? STATUS_STYLE.draft;
  const isPublished = note.status === 'published' || note.status === 'failed';

  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {note.title}
        </Typography>
        {note.version && (
          <Typography variant="caption" color="text.secondary">
            {note.version}
          </Typography>
        )}
      </TableCell>

      <TableCell>
        <Chip size="small" label={status.label} color={status.color} variant="outlined" />
      </TableCell>

      <TableCell>
        <Typography variant="body2">
          {AUDIENCE_LABEL[note.audience?.mode] ?? 'All users'}
        </Typography>
      </TableCell>

      <TableCell>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {note.sendEmail && <Chip size="small" label="Email" variant="outlined" />}
          {note.showModal && <Chip size="small" label="In-app" variant="outlined" />}
        </Box>
      </TableCell>

      <TableCell>
        {note.sendEmail ? (
          <Typography variant="body2">
            {note.sentCount} sent
            {note.failedCount > 0 && (
              <Typography component="span" variant="body2" color="error.main">
                {` · ${note.failedCount} failed`}
              </Typography>
            )}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        )}
      </TableCell>

      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {note.status === 'scheduled'
            ? formatDate(note.publishAt)
            : formatDate(note.publishedAt)}
        </Typography>
      </TableCell>

      <TableCell align="right">
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => onEdit(note)}>
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {!isPublished && (
          <Tooltip title="Publish now">
            <IconButton size="small" onClick={() => onPublish(note)}>
              <SendOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {isPublished && note.sendEmail && (
          <Tooltip title="Resend email">
            <IconButton size="small" onClick={() => onResend(note)}>
              <ReplayOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="Delete">
          {/* The only destructive action in the row — coloured to match. */}
          <IconButton size="small" color="error" onClick={() => onDelete(note)}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

export default ReleaseNoteRow;
